import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Mail, Phone } from "lucide-react";
import ReminderSignup from "@/components/ReminderSignup";

export default function Footer() {
  return (
    <footer className="bg-[color:var(--tbb-ink)] text-white mt-32 pb-24 lg:pb-0" data-testid="site-footer">
      {/* Newsletter — 1 of 3 cutoff-reminder capture points (postcode-checker + exit-intent are the others).
          All three POST to /leads/reminder-signup so ops sees a single deduped list. */}
      <div className="border-b border-white/10 bg-[color:var(--tbb-burgundy)]/10">
        <div className="tbb-container py-10 md:py-12 grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-8 items-center">
          <div>
            <div className="font-display text-2xl md:text-3xl leading-tight" data-testid="footer-newsletter-heading">
              Never miss the cutoff
            </div>
            <div className="text-sm text-white/70 mt-2 max-w-md">
              Thursday 11pm sneaks up on everyone. Get one reminder and never miss the Saturday drop.
            </div>
          </div>
          <div>
            <ReminderSignup
              source="footer"
              variant="dark"
              label=""
              buttonLabel="Sign up"
              microcopy="One reminder before Thursday 11pm. No spam, unsubscribe anytime."
              testidPrefix="footer-newsletter"
            />
          </div>
        </div>
      </div>

      <div className="tbb-container py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="font-display text-xl leading-tight mb-4">
            THE BUTCHER<br />
            <span className="text-[color:var(--tbb-burgundy)]">BOX</span>
            <span className="block text-[9px] tracking-[0.24em] font-bold text-white/50 mt-0.5">MALAGA · PERTH WA</span>
          </div>
          <p className="text-sm text-white/70 max-w-xs leading-relaxed">
            Premium Australian meat delivered cold across Perth metro. Restaurant-quality beef, lamb, chicken, burgers, smallgoods & gourmet meats.
          </p>
          <div className="flex gap-3 mt-6">
            <a href="https://www.instagram.com/thebutcherbox_au/" target="_blank" rel="noopener noreferrer" aria-label="Instagram — @thebutcherbox_au" className="p-2 border border-white/20 hover:bg-white hover:text-black transition" data-testid="footer-instagram"><Instagram size={16} /></a>
            <a href="https://www.facebook.com/people/The-Butcher-Box/61592488988258/" target="_blank" rel="noopener noreferrer" aria-label="Facebook — The Butcher Box" className="p-2 border border-white/20 hover:bg-white hover:text-black transition" data-testid="footer-facebook"><Facebook size={16} /></a>
          </div>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/boxes" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-boxes">Signature Boxes</Link></li>
            <li><Link to="/build-your-own" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-byo">Build Your Own</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-hiw">How It Works</Link></li>
            <li><Link to="/meat-delivery-perth" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-meat-delivery-perth">Perth Delivery</Link></li>
            <li><Link to="/south-african-butcher-perth" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-sa-butcher">South African & Biltong</Link></li>
            <li><Link to="/halal-meat-delivery-perth" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-halal">Halal Meat Delivery</Link></li>
            <li><Link to="/meat-delivery-bunbury" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-bunbury">Bunbury Delivery</Link></li>
            <li><Link to="/meat-subscription-box" className="hover:text-[color:var(--tbb-burgundy)]">Subscription Box</Link></li>
            <li><Link to="/bbq-meat-box" className="hover:text-[color:var(--tbb-burgundy)]">BBQ Meat Box</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Trade & Info</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/wholesale" className="hover:text-[color:var(--tbb-burgundy)]">Wholesale</Link></li>
            <li><Link to="/export" className="hover:text-[color:var(--tbb-burgundy)]">Export</Link></li>
            <li><Link to="/faq" className="hover:text-[color:var(--tbb-burgundy)]">FAQ</Link></li>
            <li><Link to="/blog" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-blog">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-contact">Contact</Link></li>
            <li className="text-white/60">Delivery: Perth metro (6000–6199) · <Link to="/meat-delivery-bunbury" className="text-white/80 hover:text-[color:var(--tbb-burgundy)] underline decoration-white/20 underline-offset-2" data-testid="footer-bunbury-inline">Bunbury &amp; South West monthly</Link></li>
            <li className="text-[color:var(--tbb-burgundy)] font-bold">Order Thu 11pm · Delivered Sat</li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Get In Touch</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 shrink-0 text-[color:var(--tbb-burgundy)]" />
              <span>
                15 Action Rd<br />
                Malaga WA 6090<br />
                <Link to="/contact" className="text-white/50 text-xs hover:text-[color:var(--tbb-burgundy)]" data-testid="footer-map-link">View on map →</Link>
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={14} className="shrink-0 text-[color:var(--tbb-burgundy)]" />
              <a href="mailto:orders@thebutcherbox.com.au" className="hover:text-white" data-testid="footer-email">orders@thebutcherbox.com.au</a>
            </li>
            <li className="flex items-center gap-2">
              <Phone size={14} className="shrink-0 text-[color:var(--tbb-burgundy)]" />
              <a href="tel:+61479044566" className="hover:text-white" data-testid="footer-phone">0479 044 566</a>
            </li>
            <li className="text-xs text-white/50 pt-2 border-t border-white/10 space-y-1">
              <div data-testid="footer-hours">Support hours: Mon–Fri 9am–5pm AWST</div>
              <div data-testid="footer-hours-delivery">Delivery-day support: Sat 7am–1pm</div>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="tbb-container py-6 flex flex-col md:flex-row items-center justify-between text-xs text-white/50 gap-2">
          <span>© {new Date().getFullYear()} THE BUTCHER BOX — All rights reserved.</span>
          <span>Proudly WA Owned · Perth Local · Australian Made</span>
        </div>
      </div>
    </footer>
  );
}
